import { componentSizes } from '@/theme/componentSizes';
import { resolveSemanticColor, type SemanticColorKey } from '@/theme/semanticTokens';
import { type RadiusToken, type SpacingToken } from '@/theme/tokens';
import { r, s } from '@/theme/tokens';
import { ButtonBase } from './ButtonBase';
import { useTheme } from './hooks';
import type { ButtonBaseProps } from './ButtonBase';
import type { SurfaceBackground } from './Surface';
import type { SxProps, Theme } from './types';
import type { ReactElement, ReactNode } from 'react';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const RADIUS_MAP: Record<RadiusToken, string> = {
  none: r('none'),
  xs: r('xs'),
  sm: r('sm'),
  chip: r('chip'),
  md: r('md'),
  lg: r('lg'),
  card: r('card'),
  panel: r('panel'),
};

const resolveSpacing = (token: SpacingToken | null | undefined): string | undefined =>
  token ? SPACING_MAP[token] : undefined;

type SurfaceButtonProps = Omit<ButtonBaseProps, 'children' | 'sx'> & {
  children: ReactNode;
  padding?: SpacingToken | null;
  radius?: RadiusToken;
  border?: boolean;
  background?: SurfaceBackground;
  sx?: SxProps<Theme>;
};

const SURFACE_BUTTON_BACKGROUND_COLOR_KEYS: Record<
  Exclude<SurfaceBackground, 'transparent'>,
  SemanticColorKey
> = {
  surface: 'surface',
  page: 'surfacePage',
  primary: 'surface',
  secondary: 'surfaceInset',
  inset: 'surfaceInset',
  hover: 'surfaceHover',
  overlay: 'surfaceOverlay',
};

const resolveSurfaceButtonBackground = (theme: Theme, background: SurfaceBackground): string =>
  background === 'transparent'
    ? 'transparent'
    : resolveSemanticColor(theme, SURFACE_BUTTON_BACKGROUND_COLOR_KEYS[background]);

export const SurfaceButton = ({
  children,
  padding = 'md',
  radius = 'card',
  border = true,
  background = 'surface',
  sx,
  ...rest
}: SurfaceButtonProps): ReactElement => {
  const theme = useTheme();
  const resolvedBackground = resolveSurfaceButtonBackground(theme, background);
  const baseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    display: 'block',
    fontSize: componentSizes.buttons.fontSize,
    padding: resolveSpacing(padding),
    textAlign: 'inherit',
    color: 'inherit',
    borderRadius: RADIUS_MAP[radius],
    border: border ? `1px solid ${resolveSemanticColor(theme, 'border')}` : 'none',
    boxSizing: 'border-box',
    backgroundColor: resolvedBackground,
    WebkitTapHighlightColor: 'transparent',
    overflow: 'hidden',
    '&:hover, &.Mui-focusVisible, &:active': {
      backgroundColor: resolvedBackground,
    },
  };
  const resolvedSx = sx ? [baseSx, ...(Array.isArray(sx) ? sx : [sx])] : baseSx;

  return (
    <ButtonBase sx={resolvedSx} {...rest}>
      {children}
    </ButtonBase>
  );
};
